import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Materials/MaterialTransactionModal.css';

import {
  logMaterialUsage,
  fetchMaterialsByProject,
  undoMaterialUsage
} from '../../../../../../../Redux/Projects/Material/materialSlice';

import { showFeedback } from '../../../../../../../Redux/UI/uiFeedbackSlice';

import { storage } from '../../../../../../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';

const MaterialTransactionModal = ({
  mode = 'log',
  material,
  projectId,
  prefill = null,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { materials } = useSelector((state) => state.materials || { materials: [] });

  const selectedMaterial = useMemo(() => {
    return materials.find((m) => String(m._id) === String(material?._id)) || material || null;
  }, [materials, material]);

  const availableStock = useMemo(() => {
    if (!selectedMaterial) return 0;
    return (Number(selectedMaterial.received || 0) - Number(selectedMaterial.used || 0));
  }, [selectedMaterial]);

  // lock quantity if requested
  const qtyLocked = Boolean(prefill?.lockQuantity && prefill?.quantity != null);

  // ---- state
  const [quantity, setQuantity] = useState('');
  const [person, setPerson] = useState('');
  const [usedFor, setUsedFor] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // track user edits so auto-fill doesn't override
  const touched = useRef({
    quantity: false,
    person: false,
    usedFor: false,
    date: false,
    notes: false,
  });

  // apply prefill ONCE per open
  const prefillKey = useMemo(() => {
    const mid = material?._id || '';
    const q = prefill?.quantity ?? '';
    const l = prefill?.lockQuantity ? 1 : 0;
    return `${mid}-${q}-${l}`;
  }, [material?._id, prefill?.quantity, prefill?.lockQuantity]);

  useEffect(() => {
    // reset touched on new open
    touched.current = { quantity: false, person: false, usedFor: false, date: false, notes: false };

    // 1) apply prefill first
    if (prefill?.quantity != null) setQuantity(String(prefill.quantity));
    else setQuantity('');

    if (prefill?.usedBy != null) setPerson(String(prefill.usedBy));
    else setPerson('');

    if (prefill?.usedFor != null) setUsedFor(String(prefill.usedFor));
    else setUsedFor('');

    if (prefill?.date) setDate(String(prefill.date));
    else setDate(new Date().toISOString().split('T')[0]);

    if (prefill?.notes != null) setNotes(String(prefill.notes));
    else setNotes('');

    setPhoto(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillKey]);

  // 2) fallback from last usage ONLY if field not touched AND no prefill locked it
  useEffect(() => {
    if (!selectedMaterial) return;

    // person fallback
    if (!touched.current.person && !person && selectedMaterial?.lastUsage?.usedBy) {
      setPerson(String(selectedMaterial.lastUsage.usedBy));
    }

    // quantity fallback ONLY when not locked AND empty AND not touched
    if (!qtyLocked && !touched.current.quantity && !quantity && selectedMaterial?.lastUsage?.quantity != null) {
      setQuantity(String(selectedMaterial.lastUsage.quantity));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMaterial]);

  const resetAndClose = () => {
    setSubmitting(false);
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!selectedMaterial) {
      dispatch(showFeedback({ type: 'error', title: 'No material', message: 'Material not found.' }));
      return;
    }

    const qtyNum = Number(quantity);
    if (!Number.isFinite(qtyNum) || qtyNum <= 0) {
      dispatch(showFeedback({ type: 'warning', title: 'Invalid quantity', message: 'Enter a valid quantity.' }));
      return;
    }

    if (qtyNum > availableStock) {
      dispatch(showFeedback({
        type: 'error',
        title: 'Insufficient stock',
        message: `Available: ${availableStock} ${selectedMaterial.unit || ''}`.trim(),
      }));
      return;
    }

    if (!person || !usedFor || !date) {
      dispatch(showFeedback({
        type: 'warning',
        title: 'Missing fields',
        message: 'Fill Used By, Used For, and Date.',
      }));
      return;
    }

    setSubmitting(true);

    try {
      let fileUrl = '';
      if (photo) {
        const fileRef = ref(storage, `material-usage/${uuidv4()}-${photo.name}`);
        await uploadBytes(fileRef, photo);
        fileUrl = await getDownloadURL(fileRef);
      }

      const payload = {
        materialId: selectedMaterial._id,
        projectId,
        quantity: qtyNum,
        date,
        notes,
        fileUrl: fileUrl || null,
        usedBy: person,
        usedFor,
        type: 'usage',
      };

      const action = await dispatch(logMaterialUsage(payload));
      if (logMaterialUsage.fulfilled.match(action)) {
        dispatch(showFeedback({ type: 'success', title: 'Usage logged', message: 'Material usage logged successfully.' }));
        dispatch(fetchMaterialsByProject(projectId));
        resetAndClose();
        return;
      }

      const msg = action.payload?.message || action.error?.message || 'Failed to log usage.';
      dispatch(showFeedback({ type: 'error', title: 'Failed', message: msg }));
    } catch (err) {
      dispatch(showFeedback({ type: 'error', title: 'Error', message: err?.message || 'Error submitting transaction.' }));
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Undo: double click confirm (no browser confirm)
  const undoArmed = useRef(false);
  const undoTimer = useRef(null);

  const armUndo = () => {
    undoArmed.current = true;
    dispatch(showFeedback({
      type: 'warning',
      title: 'Confirm undo',
      message: 'Click "Undo Last Usage" again within 4 seconds to confirm.',
    }));
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => { undoArmed.current = false; }, 4000);
  };

  const handleUndoLastUsage = async () => {
    if (!selectedMaterial?._id) {
      dispatch(showFeedback({ type: 'error', title: 'No material', message: 'No selected material to undo.' }));
      return;
    }

    if (!undoArmed.current) {
      armUndo();
      return;
    }

    // confirmed
    undoArmed.current = false;
    if (undoTimer.current) clearTimeout(undoTimer.current);

    try {
      const action = await dispatch(undoMaterialUsage({ materialId: selectedMaterial._id, projectId }));
      if (undoMaterialUsage.fulfilled.match(action)) {
        dispatch(showFeedback({ type: 'success', title: 'Undone', message: 'Last usage entry undone.' }));
        dispatch(fetchMaterialsByProject(projectId));
        resetAndClose();
        return;
      }
      const msg = action.payload?.message || action.error?.message || 'Failed to undo usage.';
      dispatch(showFeedback({ type: 'error', title: 'Undo failed', message: msg }));
    } catch (err) {
      dispatch(showFeedback({ type: 'error', title: 'Undo failed', message: err?.message || 'Failed to undo usage.' }));
    }
  };

  if (!selectedMaterial) return null;

  return (
    <div className="material-modal-overlay" onMouseDown={resetAndClose}>
      <div className="material-modal large" onMouseDown={(e) => e.stopPropagation()}>
        <div className="material-modal-header">
          <h3>Log Material Usage</h3>
          <button type="button" className="material-modal-close" onClick={resetAndClose}>×</button>
        </div>

        <div className="material-modal-body grid">
          <div>
            <label>Material Name</label>
            <input type="text" value={selectedMaterial.name} readOnly />
          </div>

          <div>
            <label>Available Stock</label>
            <input type="text" value={`${availableStock} ${selectedMaterial.unit || ''}`.trim()} readOnly />
          </div>

          <div>
            <label>Quantity Used</label>
            {qtyLocked ? (
              <input type="text" value={`${quantity} ${selectedMaterial.unit || ''}`.trim()} readOnly />
            ) : (
              <input
                type="number"
                placeholder="e.g., 40"
                value={quantity}
                min={1}
                max={availableStock}
                onChange={(e) => {
                  touched.current.quantity = true;
                  setQuantity(e.target.value);
                }}
              />
            )}
          </div>

          <div>
            <label>Used By</label>
            <input
              type="text"
              placeholder="e.g., Rajesh"
              value={person}
              onChange={(e) => {
                touched.current.person = true;
                setPerson(e.target.value);
              }}
            />
          </div>

          <div>
            <label>Used For / Location</label>
            <input
              type="text"
              placeholder="e.g., Slab casting, Block B"
              value={usedFor}
              onChange={(e) => {
                touched.current.usedFor = true;
                setUsedFor(e.target.value);
              }}
            />
          </div>

          <div>
            <label>Date of Usage</label>
            <input
              type="date"
              value={date}
              onChange={(e) => {
                touched.current.date = true;
                setDate(e.target.value);
              }}
            />
          </div>

          <div>
            <label>Upload Photo</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPhoto(e.target.files?.[0] || null)}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <label>Notes (optional)</label>
            <textarea
              placeholder="Additional info"
              value={notes}
              onChange={(e) => {
                touched.current.notes = true;
                setNotes(e.target.value);
              }}
            />
          </div>
        </div>

        <div className="material-modal-footer">
          <button type="button" className="submit-btn" onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>

          {selectedMaterial?.lastUsageCanUndo && (
            <button type="button" className="undo-btn danger" onClick={handleUndoLastUsage} disabled={submitting}>
              Undo Last Usage
            </button>
          )}

          <button type="button" className="cancel-btn" onClick={resetAndClose} disabled={submitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MaterialTransactionModal;
